import { useMemo, useState } from "react";
import { plural } from "@/lib/plural";
import { AdminTooltip } from "@/components/admin/AdminTooltip";

import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  Pencil,
  Trash2,
  Plus,
  Loader2,
  Pause,
  Play,
  Sparkles,
  BarChart3,
  Eye,
  MessageSquare,
  RefreshCw,
  Archive,
  ArchiveRestore,
  BadgeCheck,
  Wallet,
  HandCoins,
} from "lucide-react";
import {
  getMyAds,
  deleteAd,
  setAdPaused,
  setAdArchived,
  markAdSold,
  getMySalesSummary,
} from "@/lib/ads.functions";
import { getMyPlanStatus } from "@/lib/plans.functions";
import { getMyAdStats, renewAd } from "@/lib/ad-stats.functions";
import { AD_LIFETIME_DAYS, daysLeft } from "@/lib/ad-stats";
import { getPromotionStatus, promoteAd, cancelPromotionForAd } from "@/lib/promotions.functions";
import { PLANS, planAtLeast, remainingThisMonth } from "@/lib/plans";
import { categoryLabel } from "@/lib/categories";
import { AdCard, type AdCardData } from "@/components/AdCard";
import { useTopBids } from "@/hooks/useTopBids";
import { DraftCard } from "@/components/DraftCard";
import { Button } from "@/components/ui/button";
import { HelpLink } from "@/components/help/HelpLink";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  promotionDayOptions,
  maxPromotionDays,
  promotionStepNote,
} from "@/lib/promotion-days";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { setAdOffers } from "@/lib/offers.functions";
import {
  myAdsQuery,
  myPlanQuery,
  myPromotionsQuery,
  myAdStatsQuery,
  mySalesQuery,
} from "@/lib/queries";

export const Route = createFileRoute("/_authenticated/mina-annonser")({
  head: () => ({
    meta: [
      { title: "Mina annonser — Sellavo" },
      { name: "description", content: "Hantera, arkivera och sälj dina egna annonser på Sellavo." },
      { property: "og:title", content: "Mina annonser — Sellavo" },
      {
        property: "og:description",
        content: "Hantera, arkivera och sälj dina egna annonser på Sellavo.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex" },
    ],
  }),
  loader: async ({ context }) => {
    await Promise.all([
      context.queryClient.ensureQueryData(myAdsQuery()),
      context.queryClient.ensureQueryData(myPlanQuery()),
      context.queryClient.ensureQueryData(myPromotionsQuery()),
      context.queryClient.ensureQueryData(myAdStatsQuery()),
      context.queryClient.ensureQueryData(mySalesQuery()),
    ]);
  },
  component: MyAds,
});

type MyAd = AdCardData & {
  is_paused?: boolean;
  hidden?: boolean;
  accepts_offers?: boolean;
  sale_kind?: string | null;
  archived_at?: string | null;
  sold_at?: string | null;
  sold_price?: number | null;
};

const kr = (n: number) => `${n.toLocaleString("sv-SE")} kr`;
const dateFmt = (iso: string) =>
  new Date(iso).toLocaleDateString("sv-SE", { day: "numeric", month: "short", year: "numeric" });

function MyAds() {
  const removeAd = useServerFn(deleteAd);
  const togglePause = useServerFn(setAdPaused);
  const toggleArchive = useServerFn(setAdArchived);
  const sellAd = useServerFn(markAdSold);
  const doRenew = useServerFn(renewAd);
  const runPromote = useServerFn(promoteAd);
  const runUnpromote = useServerFn(cancelPromotionForAd);
  const setOffersFn = useServerFn(setAdOffers);
  const queryClient = useQueryClient();

  const [promoMsg, setPromoMsg] = useState<string | null>(null);
  const [promoteFor, setPromoteFor] = useState<MyAd | null>(null);
  const [promoteDays, setPromoteDays] = useState(1);
  const [unpromoteFor, setUnpromoteFor] = useState<MyAd | null>(null);
  const [archiveFor, setArchiveFor] = useState<MyAd | null>(null);
  const [pauseFor, setPauseFor] = useState<MyAd | null>(null);
  const [removeFor, setRemoveFor] = useState<MyAd | null>(null);
  const [soldPrice, setSoldPrice] = useState("");
  // Återställning ur arkivet av en annons som är markerad som såld.
  const [restoreFor, setRestoreFor] = useState<MyAd | null>(null);
  const [restoreBlocked, setRestoreBlocked] = useState(false);

  const { data, isLoading } = useQuery(myAdsQuery());
  const { data: planStatus } = useQuery(myPlanQuery());
  const { data: promoStatus } = useQuery(myPromotionsQuery());
  const { data: stats } = useQuery(myAdStatsQuery());
  const { data: sales } = useQuery(mySalesQuery());
  const statsBySlug = new Map((stats?.ads ?? []).map((s) => [s.slug, s]));

  const refreshAll = () => {
    for (const key of [["my-ads"], ["my-ad-stats"], ["my-promotions"], ["my-sales"], ["ads"]])
      queryClient.invalidateQueries({ queryKey: key });
  };

  const renew = useMutation({
    mutationFn: (slug: string) => doRenew({ data: { slug } }),
    onSuccess: refreshAll,
  });

  const promote = useMutation({
    mutationFn: (vars: { slug: string; days: number }) => runPromote({ data: vars }),
    onSuccess: (res) => {
      setPromoteFor(null);
      setPromoMsg(res.ok ? `Annonsen är framlyft i ${res.days} dygn.` : res.message);
      refreshAll();
    },
    onError: () => setPromoMsg("Kunde inte lyfta annonsen just nu."),
  });

  const unpromote = useMutation({
    mutationFn: (slug: string) => runUnpromote({ data: { slug } }),
    onSuccess: (res) => {
      setUnpromoteFor(null);
      setPromoMsg(res.message);
      refreshAll();
    },
    onError: () => setPromoMsg("Kunde inte ta bort framlyftet just nu."),
  });

  const offers = useMutation({
    mutationFn: (vars: { slug: string; accepts: boolean }) => setOffersFn({ data: vars }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["my-ads"] });
    },
  });

  const pause = useMutation({
    mutationFn: (vars: { slug: string; paused: boolean }) => togglePause({ data: vars }),
    onSuccess: (res: any) => {
      setPauseFor(null);
      if (res?.refundedPromotions)
        setPromoMsg(
          `Annonsen är pausad. ${res.refundedPromotions} lyftdagar lades tillbaka i din kvot.`,
        );
      refreshAll();
    },
  });

  const archive = useMutation({
    mutationFn: (vars: { slug: string; archived: boolean; clearSold?: boolean }) =>
      toggleArchive({ data: vars }),
    onSuccess: () => {
      setArchiveFor(null);
      setRestoreFor(null);
      setRestoreBlocked(false);
      refreshAll();
    },
  });

  const sell = useMutation({
    mutationFn: (vars: { slug: string; soldPrice?: number }) => sellAd({ data: vars }),
    onSuccess: () => {
      setRemoveFor(null);
      setSoldPrice("");
      refreshAll();
    },
  });

  const del = useMutation({
    mutationFn: (slug: string) => removeAd({ data: { slug } }),
    onSuccess: () => {
      setRemoveFor(null);
      refreshAll();
    },
  });

  const ads = (data ?? []) as MyAd[];
  const active = useMemo(() => ads.filter((a) => !a.archived_at), [ads]);
  const topBids = useTopBids(ads);
  const archived = useMemo(() => ads.filter((a) => !!a.archived_at), [ads]);

  const isPromoted = (ad: MyAd) =>
    !!ad.promoted_until && new Date(ad.promoted_until).getTime() > Date.now();
  const promoDaysLeft = (ad: MyAd) =>
    Math.max(
      1,
      Math.ceil((new Date(ad.promoted_until as string).getTime() - Date.now()) / 86_400_000),
    );
  const plan = planStatus?.plan ?? "free";
  const remaining = planStatus ? remainingThisMonth(plan, planStatus.usage) : null;
  const promoStep = promoStatus?.step ?? promoStatus?.days ?? 1;
  const daysLeftQuota = promoStatus?.remaining ?? 0;
  const promoDayOptions = promotionDayOptions(promoStep, daysLeftQuota);
  const maxSelectableDays = maxPromotionDays(promoStep, daysLeftQuota);

  const openPromote = (ad: MyAd) => {
    setPromoMsg(null);
    setPromoteDays(promoStep);
    setPromoteFor(ad);
  };

  return (
    <main className="mx-auto w-full max-w-5xl px-4 sm:px-6 lg:px-8 pb-16 pt-6 sm:pb-20 sm:pt-10">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight text-foreground">
            Mina annonser
          </h1>
          <p className="mt-2 text-muted-foreground">
            Redigera text, pris och kontaktuppgifter när du vill.
          </p>
        </div>
        <div className="flex gap-2">
          <Button asChild variant="outline">
            <Link to="/statistik">
              <BarChart3 className="h-4 w-4" /> Statistik
            </Link>
          </Button>
          <Button asChild>
            <Link to="/">
              <Plus className="h-4 w-4" /> Ny annons
            </Link>
          </Button>
        </div>
      </div>

      <DraftCard />



      {planStatus && (
        <div className="mt-6 flex flex-wrap items-center gap-3 rounded-md border border-border bg-card px-4 py-3 text-sm">
          <span className="rounded-md bg-primary/10 px-2 py-1 font-semibold text-primary">
            {PLANS[plan].name}
          </span>
          <span className="text-muted-foreground">
            {remaining === null
              ? `Obegränsat antal annonser (max ${PLANS[plan].limits.perDay} per dygn).`
              : `${remaining} av ${PLANS[plan].limits.perMonth} annonser kvar den här månaden.`}
          </span>
          {!planAtLeast(plan, "pro") && (
            <Link
              to="/priser"
              className="ml-auto font-semibold text-primary underline underline-offset-2"
            >
              Se planer
            </Link>
          )}
        </div>
      )}

      {promoStatus && (
        <div className="mt-3 flex flex-wrap items-center gap-3 rounded-md border border-border bg-card px-4 py-3 text-sm">
          <Sparkles className="h-4 w-4 shrink-0 text-primary" />
          <span className="text-muted-foreground">
            {promoStatus.limit === 0
              ? "Lyft fram annonser högst upp i listan med Plus eller Pro."
              : `${promoStatus.remaining} av ${promoStatus.limit} lyftdagar kvar den här månaden · steg om ${promoStep} dygn per lyft.`}
          </span>
          <Link
            to="/lyft"
            className="ml-auto font-semibold text-primary underline underline-offset-2"
          >
            Hantera lyft
          </Link>
        </div>
      )}
      {promoMsg && <p className="mt-2 text-sm text-foreground">{promoMsg}</p>}

      <div className="mt-8">
        {isLoading ? (
          <p className="text-muted-foreground">Laddar…</p>
        ) : ads.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border p-10 text-center">
            <p className="text-muted-foreground">Du har inga annonser ännu.</p>
            <Button asChild className="mt-4">
              <Link to="/">Skapa din första annons</Link>
            </Button>
          </div>
        ) : active.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border p-10 text-center">
            <p className="text-muted-foreground">Alla dina annonser ligger i arkivet.</p>
          </div>
        ) : (
          <div className="grid items-stretch gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {active.map((ad) => {
              const auctionLocked = !!(ad as { auctionLock?: { locked?: boolean } }).auctionLock
                ?.locked;
              return (
              <div key={ad.slug} className="flex h-full flex-col gap-2">
                <div className="relative flex-1">

                  {ad.hidden && (
                    <span className="absolute right-2 top-2 z-10 inline-flex h-7 items-center gap-1 rounded-md bg-amber-400/90 px-2 text-xs font-semibold text-ocean-900">
                      Dold annons
                    </span>
                  )}
                  {ad.is_paused && (
                    <span className="absolute left-2 top-2 z-10 inline-flex h-7 items-center rounded-md bg-foreground/85 px-2 text-xs font-semibold text-background">
                      Pausad
                    </span>
                  )}
                  <div className={`h-full ${ad.is_paused ? "opacity-60" : ""}`}>
                    <AdCard ad={ad} topBid={topBids.get(ad.slug) ?? null} />
                  </div>
                </div>
                {(() => {
                  const st = statsBySlug.get(ad.slug);
                  const left = st ? daysLeft(st.expires_at) : null;
                  const expired = left !== null && left <= 0;
                  return (
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
                      <span className="inline-flex items-center gap-1">
                        <Eye className="h-3.5 w-3.5" /> {plural(st?.views ?? 0, "visning", "visningar")}
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <MessageSquare className="h-3.5 w-3.5" />{" "}
                        {plural(st?.contacts ?? 0, "kontakt", "kontakter")}
                      </span>
                      {left !== null && (
                        <span
                          className={
                            expired || left <= 7 ? "font-semibold text-amber-600" : undefined
                          }
                        >
                          {expired ? "Utgången" : `${plural(left, "dag", "dagar")} kvar`}
                        </span>
                      )}
                      {left !== null && (expired || left <= 7) && (
                        <button
                          type="button"
                          disabled={renew.isPending}
                          onClick={() => renew.mutate(ad.slug)}
                          className="ml-auto inline-flex items-center gap-1 font-semibold text-primary underline underline-offset-2"
                          title={`Ge annonsen ${AD_LIFETIME_DAYS} nya dagar`}
                        >
                          <RefreshCw className="h-3.5 w-3.5" /> Förnya
                        </button>
                      )}
                    </div>
                  );
                })()}
                {isPromoted(ad) && (
                  <p className="inline-flex items-center gap-1.5 rounded-md bg-primary/10 px-3 py-1.5 text-xs font-semibold text-primary">
                    <Sparkles className="h-3.5 w-3.5" /> Framlyft{" "}
                    {plural(promoDaysLeft(ad), "dag", "dagar")} till – t.o.m.{" "}
                    {dateFmt(ad.promoted_until as string)}
                  </p>
                )}
                {auctionLocked && (
                  <p className="rounded-md bg-amber-500/10 px-3 py-1.5 text-xs font-semibold text-amber-700 dark:text-amber-300">
                    Auktion med bud – villkoren är låsta tills den avslutas.
                  </p>
                )}
                <div className="flex gap-2">
                  <AdminTooltip text="Ändra rubrik, text, pris, bilder och ort på annonsen.">
                    <Button asChild variant="outline" size="sm" className="flex-1">
                      <Link to="/redigera/$slug" params={{ slug: ad.slug }}>
                        <Pencil className="h-4 w-4" /> Redigera
                      </Link>
                    </Button>
                  </AdminTooltip>
                  {ad.sale_kind !== "auction" && (
                    <AdminTooltip
                      text={
                        ad.accepts_offers === false
                          ? "Bud är avstängt – slå på för att låta köpare föreslå ett eget pris."
                          : "Köpare kan lägga bud på annonsen. Klicka för att stänga av."
                      }
                    >
                      <Button
                        variant="outline"
                        size="sm"
                        aria-label="Ta emot bud"
                        disabled={offers.isPending}
                        className={ad.accepts_offers === false ? "opacity-60" : "border-primary/50 text-primary"}
                        onClick={() =>
                          offers.mutate({ slug: ad.slug, accepts: ad.accepts_offers === false })
                        }
                      >
                        <HandCoins className="h-4 w-4" />
                      </Button>
                    </AdminTooltip>
                  )}
                  <AdminTooltip
                    text={
                      auctionLocked
                        ? "Auktionen har bud – den kan inte pausas."
                        : ad.is_paused
                          ? "Återaktivera annonsen så att den syns publikt igen."
                          : "Pausa annonsen – den döljs för andra men ligger kvar hos dig."
                    }
                  >
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={pause.isPending || auctionLocked}
                      onClick={() =>
                        ad.is_paused
                          ? pause.mutate({ slug: ad.slug, paused: false })
                          : setPauseFor(ad)
                      }
                    >
                      {ad.is_paused ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
                    </Button>
                  </AdminTooltip>
                  <AdminTooltip
                    text={
                      isPromoted(ad)
                        ? "Ta bort framlyftet – annonsen hamnar tillbaka i vanlig ordning."
                        : "Lyft fram annonsen högst upp i listan under ett antal dygn."
                    }
                  >
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => (isPromoted(ad) ? setUnpromoteFor(ad) : openPromote(ad))}
                    >
                      <Sparkles className={`h-4 w-4 ${isPromoted(ad) ? "text-primary" : ""}`} />
                    </Button>
                  </AdminTooltip>
                  <AdminTooltip
                    text={
                      auctionLocked
                        ? "Auktionen har bud – den kan inte arkiveras."
                        : "Lägg annonsen i arkivet. Den slutar synas publikt och därifrån kan du markera den som såld, plocka tillbaka den eller ta bort den helt."
                    }
                  >
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={auctionLocked}
                      onClick={() => setArchiveFor(ad)}
                    >
                      <Archive className="h-4 w-4" />
                    </Button>
                  </AdminTooltip>
                </div>
              </div>
              );
            })}

          </div>

        )}
      </div>

      {/* Arkiv */}
      {archived.length > 0 && (
        <section className="mt-12">
          <h2 className="font-heading text-2xl font-bold tracking-tight text-foreground">Arkiv</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Arkiverade annonser syns inte publikt. Här kan du plocka tillbaka dem, markera dem som
            sålda eller ta bort dem helt.
          </p>

          {sales && sales.total.count > 0 && (
            <div className="mt-5 rounded-lg border border-border bg-card p-5">
              <div className="flex flex-wrap items-center gap-3">
                <Wallet className="h-5 w-5 text-primary" />
                <p className="text-lg font-semibold text-foreground">
                  Du har sålt {sales.total.count} {sales.total.count === 1 ? "sak" : "saker"} för{" "}
                  {kr(sales.total.amount)}
                </p>
              </div>
              <div className="mt-4 grid gap-5 sm:grid-cols-2">
                <div>
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Per kategori
                  </h3>
                  <ul className="mt-2 space-y-1 text-sm">
                    {sales.byCategory.map((c) => (
                      <li key={c.category} className="flex items-center justify-between gap-3">
                        <span className="text-foreground/80">
                          {categoryLabel(c.category)}{" "}
                          <span className="text-muted-foreground">({c.count})</span>
                        </span>
                        <span className="font-semibold text-foreground">{kr(c.amount)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Per år
                  </h3>
                  <ul className="mt-2 space-y-1 text-sm">
                    {sales.byYear.map((y) => (
                      <li key={y.year} className="flex items-center justify-between gap-3">
                        <span className="text-foreground/80">
                          {y.year} <span className="text-muted-foreground">({y.count})</span>
                        </span>
                        <span className="font-semibold text-foreground">{kr(y.amount)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          <div className="mt-5 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {archived.map((ad) => (
              <div key={ad.slug} className="flex flex-col gap-2">
                <div className="relative">
                  <span
                    className={`absolute left-2 top-2 z-10 inline-flex h-7 items-center rounded-md px-2 text-xs font-semibold ${
                      ad.sold_at
                        ? "bg-primary text-primary-foreground"
                        : "bg-foreground/85 text-background"
                    }`}
                  >
                    {ad.sold_at ? "Såld" : "Arkiverad"}
                  </span>
                  <div className="opacity-70">
                    <AdCard ad={ad} topBid={topBids.get(ad.slug) ?? null} />
                  </div>
                </div>
                {ad.sold_at && (
                  <p className="inline-flex items-center gap-1.5 rounded-md bg-primary/10 px-3 py-1.5 text-xs font-semibold text-primary">
                    <BadgeCheck className="h-3.5 w-3.5" /> Såld {dateFmt(ad.sold_at)} för{" "}
                    {kr(ad.sold_price ?? ad.price)}
                  </p>
                )}
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    disabled={archive.isPending}
                    onClick={() => {
                      if (ad.sold_at) {
                        setRestoreBlocked(false);
                        setRestoreFor(ad);
                        return;
                      }
                      archive.mutate({ slug: ad.slug, archived: false });
                    }}
                    title="Publicera annonsen igen"
                  >
                    <ArchiveRestore className="h-4 w-4" /> Återställ
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-destructive hover:text-destructive"
                    onClick={() => {
                      setSoldPrice(String(ad.price ?? ""));
                      setRemoveFor(ad);
                    }}
                    title="Ta bort annonsen"
                  >
                    {del.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Lyft-modal */}
      <Dialog open={!!promoteFor} onOpenChange={(o) => !o && setPromoteFor(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Lyft fram annonsen</DialogTitle>
            <DialogDescription>
              Annonsen hamnar högst upp i listan. Varje dygn kostar en lyftdag ur din månadskvot –
              du har{" "}
              <strong>
                {daysLeftQuota} av {promoStatus?.limit ?? 0}
              </strong>{" "}
              lyftdagar kvar.
            </DialogDescription>
          </DialogHeader>

          {promoStatus?.limit === 0 ? (
            <p className="text-sm text-foreground/80">
              Lyft ingår i Plus och Pro.{" "}
              <Link
                to="/priser"
                className="font-semibold text-primary underline underline-offset-2"
              >
                Se planerna
              </Link>
              .
            </p>
          ) : maxSelectableDays === 0 ? (
            <p className="text-sm text-foreground/80">
              Du har {daysLeftQuota} lyftdagar kvar den här månaden – ett lyft är minst {promoStep}{" "}
              dygn, så kvoten räcker inte till ett nytt lyft.
            </p>
          ) : (
            <div className="space-y-3">
              <Label>Antal dygn</Label>
              <div className="flex flex-wrap gap-2">
                {promoDayOptions.map((d: number) => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => setPromoteDays(d)}
                    className={`h-10 min-w-10 rounded-md border px-3 text-sm font-semibold transition-colors ${
                      promoteDays === d
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-border bg-background text-foreground hover:bg-secondary"
                    }`}
                  >
                    {d}
                  </button>
                ))}
              </div>
              <p className="text-sm text-muted-foreground">
                {promotionStepNote(promoStep)} {promoteDays} dygn kostar {promoteDays} av dina{" "}
                {daysLeftQuota} återstående lyftdagar. Kvar efteråt: {daysLeftQuota - promoteDays}.
              </p>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setPromoteFor(null)}>
              Avbryt
            </Button>
            <Button
              disabled={
                promote.isPending || maxSelectableDays === 0 || (promoStatus?.limit ?? 0) === 0
              }
              onClick={() =>
                promoteFor && promote.mutate({ slug: promoteFor.slug, days: promoteDays })
              }
            >
              {promote.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="h-4 w-4" />
              )}
              Lyft fram i {promoteDays} dygn
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ta bort framlyft */}
      <Dialog open={!!unpromoteFor} onOpenChange={(o) => !o && setUnpromoteFor(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ta bort framlyftet?</DialogTitle>
            <DialogDescription>
              „{unpromoteFor?.title}” slutar ligga högst upp i listan. De lyftdagar som inte hunnit
              användas läggs tillbaka i din månadskvot.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUnpromoteFor(null)}>
              Behåll framlyftet
            </Button>
            <Button
              disabled={unpromote.isPending}
              onClick={() => unpromoteFor && unpromote.mutate(unpromoteFor.slug)}
            >
              {unpromote.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Ta bort
              framlyftet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pausa */}
      <Dialog open={!!pauseFor} onOpenChange={(o) => !o && setPauseFor(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Pausa annonsen?</DialogTitle>
            <DialogDescription>
              „{pauseFor?.title}” döljs för alla andra tills du aktiverar den igen.
              {pauseFor && isPromoted(pauseFor)
                ? ` Framlyftet avbryts – de ${promoDaysLeft(pauseFor)} lyftdagar som är kvar läggs tillbaka i din månadskvot.`
                : ""}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPauseFor(null)}>
              Avbryt
            </Button>
            <Button
              disabled={pause.isPending}
              onClick={() => pauseFor && pause.mutate({ slug: pauseFor.slug, paused: true })}
            >
              {pause.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Pause className="h-4 w-4" />
              )}{" "}
              Pausa annonsen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Återställ en såld annons */}
      <Dialog
        open={!!restoreFor}
        onOpenChange={(o) => {
          if (!o) {
            setRestoreFor(null);
            setRestoreBlocked(false);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{restoreBlocked ? "Sålda prylar stannar i arkivet" : "Såldes prylen?"}</DialogTitle>
            <DialogDescription>
              {restoreBlocked
                ? "Du kan inte ta tillbaka prylar som är sålda – lägg upp en ny annons i stället."
                : `„${restoreFor?.title}” är markerad som såld. Sålda affärer sparas i din försäljningsstatistik, så svaret avgör om annonsen kan bli aktiv igen.`}
            </DialogDescription>
          </DialogHeader>

          {restoreBlocked ? (
            <DialogFooter className="gap-2 sm:flex-col sm:items-stretch">
              <Button asChild>
                <Link to="/skapa">
                  <Plus className="h-4 w-4" /> Lägg upp ny annons
                </Link>
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setRestoreFor(null);
                  setRestoreBlocked(false);
                }}
              >
                Stäng
              </Button>
            </DialogFooter>
          ) : (
            <DialogFooter className="gap-2 sm:flex-col sm:items-stretch">
              <Button onClick={() => setRestoreBlocked(true)}>
                <BadgeCheck className="h-4 w-4" /> Ja, den såldes
              </Button>
              <Button
                variant="outline"
                disabled={archive.isPending}
                onClick={() =>
                  restoreFor &&
                  archive.mutate({ slug: restoreFor.slug, archived: false, clearSold: true })
                }
              >
                {archive.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <ArchiveRestore className="h-4 w-4" />
                )}{" "}
                Nej, den såldes inte
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  setRestoreFor(null);
                  setRestoreBlocked(false);
                }}
              >
                Avbryt
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* Arkivera */}
      <Dialog open={!!archiveFor} onOpenChange={(o) => !o && setArchiveFor(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Lägg annonsen i arkivet?</DialogTitle>
            <DialogDescription>
              „{archiveFor?.title}” döljs för alla andra men finns kvar hos dig. I arkivet kan du
              sedan återställa den, markera den som såld eller ta bort den helt.
              {archiveFor && isPromoted(archiveFor)
                ? ` Framlyftet avbryts – de ${promoDaysLeft(archiveFor)} lyftdagar som är kvar läggs tillbaka i din månadskvot.`
                : ""}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setArchiveFor(null)}>
              Avbryt
            </Button>
            <Button
              disabled={archive.isPending}
              onClick={() =>
                archiveFor && archive.mutate({ slug: archiveFor.slug, archived: true })
              }
            >
              {archive.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              <Archive className="h-4 w-4" /> Lägg i arkiv
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Såld eller ta bort */}
      <Dialog
        open={!!removeFor}
        onOpenChange={(o) => {
          if (!o) {
            setRemoveFor(null);
            setSoldPrice("");
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Blev den såld?</DialogTitle>
            <DialogDescription>
              Välj om „{removeFor?.title}” såldes eller bara ska bort. Sålda annonser sparas i
              arkivet och räknas in i din försäljningsstatistik.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <Label htmlFor="sold-price">Slutpris (kr)</Label>
            <Input
              id="sold-price"
              inputMode="numeric"
              value={soldPrice}
              onChange={(e) => setSoldPrice(e.target.value.replace(/[^\d]/g, ""))}
              placeholder={String(removeFor?.price ?? "")}
            />
            <p className="text-xs text-muted-foreground">
              Justera om du sålde för ett annat pris än utropspriset {kr(removeFor?.price ?? 0)}.
            </p>
          </div>

          <DialogFooter className="gap-2 sm:flex-col sm:items-stretch">
            <Button
              disabled={sell.isPending}
              onClick={() =>
                removeFor &&
                sell.mutate({
                  slug: removeFor.slug,
                  soldPrice: soldPrice ? Number(soldPrice) : removeFor.price,
                })
              }
            >
              {sell.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <BadgeCheck className="h-4 w-4" />
              )}{" "}
              Ja, den är såld
            </Button>
            <Button
              variant="outline"
              className="text-destructive hover:text-destructive"
              disabled={del.isPending}
              onClick={() => removeFor && del.mutate(removeFor.slug)}
            >
              {del.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}{" "}
              Nej, ta bort annonsen
            </Button>
            <Button variant="ghost" onClick={() => setRemoveFor(null)}>
              Avbryt
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <div className="mt-8 flex justify-center">
        <HelpLink topic="annons" />
      </div>
    </main>
  );
}
